// @flow
import React from "react";
import { Field, reduxForm, formValues, type FormProps } from "redux-form";
import { Form } from "semantic-ui-react";
import { withTranslation } from "react-i18next";

import SaveButton from "../SaveButton";
import Section from "../Section";
import { sagaConnector } from "Commons/Utilities/redux";
import { DeliveryAddressOptions } from "modules/ChangeService/misc/Options";
import { RadioGroupBooleanField } from "../Fields";
import FormLabel from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import { CategoryTypes } from "Commons/Modules/category";
import { COMPANY } from "Commons/Utilities/Constants/PropertyTypes";
import {
  CompanyName,
  TaxId,
  StreetAndStreetNumber,
  SalutationFields,
  FirstName,
  LastName,
  PostalCodeAndLocation,
} from "modules/ChangeService/components/FormComponents";

import type { ChangeServiceT } from "modules/ChangeService/misc/Types";

type FieldsT = {
  salutation: string,
  salutationTitle: string,
  firstName: string,
  lastName: string,
  companyName: string,
  taxId?: string,
  street: string,
  streetNumber: string,
  postalCode: string,
  locationId: string,
  locationMainName: string,
  locationName: string,
  locationPartName: string,
};

type AddressFormT = {|
  t: typeof TFunction,
  initialValues: ChangeServiceT | {},
  currentValues: ChangeServiceT,
  fields: FieldsT,
  form: string,
  change: $PropertyType<FormProps, "change">,
  dataCy: string,
  mobileChangeService?: boolean,
  divider?: boolean,
|};

export const AddressForm = (props: AddressFormT) => {
  const { t, initialValues, currentValues, fields, form, change, mobileChangeService = false, dataCy, divider } = props;

  // This value ultimately used for the API endpoint.
  const category = initialValues.category === CategoryTypes.GAS ? "gas" : "electricity";

  return (
    <div data-cy={dataCy}>
      <SalutationFields divider={divider} t={t} fields={fields} mobileChangeService={mobileChangeService} />
      <FirstName t={t} divider={divider} fields={fields} />
      <LastName t={t} divider={divider} fields={fields} />
      {currentValues.propertyType === COMPANY && (
        <>
          <CompanyName divider={divider} fields={fields} t={t} />
          {!!fields.taxId && <TaxId divider={divider} fields={fields} t={t} />}
        </>
      )}
      <PostalCodeAndLocation
        fields={fields}
        t={t}
        divider={divider}
        category={category}
        initialValues={initialValues}
        form={form}
        change={change}
      />
      <StreetAndStreetNumber
        form={form}
        divider={divider}
        t={t}
        fields={fields}
        initialValues={initialValues}
        change={change}
        mobileChangeService={mobileChangeService}
      />
    </div>
  );
};

type AlternativeDeliveryAddressT = {|
  t: typeof TFunction,
  initialValues: ChangeServiceT,
  currentValues: ChangeServiceT,
  billingAddressSameAsDeliveryAddress: boolean,
  fields: FieldsT,
  form: string,
  change: $PropertyType<FormProps, "change">,
  divider?: boolean,
|};

let AlternativeDeliveryAddressSection = (props: AlternativeDeliveryAddressT) => {
  const { t, initialValues, billingAddressSameAsDeliveryAddress, fields, form, change, currentValues, divider } = props;

  if (billingAddressSameAsDeliveryAddress === false) {
    return (
      <AddressForm
        fields={fields}
        currentValues={currentValues}
        initialValues={initialValues}
        t={t}
        form={form}
        change={change}
        divider={divider}
        dataCy="alternativeDeliveryAddress"
      />
    );
  }

  return null;
};

const AlternativeDeliveryAddress = formValues("billingAddressSameAsDeliveryAddress")(AlternativeDeliveryAddressSection);

type DeliveryAddressT = {
  t: typeof TFunction,
  initialValues: ChangeServiceT,
  currentValues: ChangeServiceT,
  hideSaveButton?: boolean,
} & FormProps;

let DeliveryAddress = (props: DeliveryAddressT) => {
  const {
    t,
    initialValues = {},
    currentValues,
    submitFailed,
    invalid,
    form,
    submitting,
    submitSucceeded,
    change,
    handleSubmit,
    hideSaveButton = false,
  } = props;

  const addressFields = {
    salutation: "salutation",
    salutationTitle: "salutationTitle",
    firstName: "firstname",
    lastName: "lastname",
    companyName: "companyName",
    taxId: "taxId",
    street: "street",
    streetNumber: "streetNumber",
    postalCode: "postalcode",
    locationId: "locationId",
    locationMainName: "locationMainName",
    locationName: "location",
    locationPartName: "locationPartName",
  };

  const deliveryfields: FieldsT = {
    salutation: "differentSalutation",
    salutationTitle: "differentSalutationTitle",
    firstName: "differentFirstname",
    lastName: "differentLastname",
    companyName: "differentCompanyName",
    street: "differentStreet",
    streetNumber: "differentStreetNumber",
    postalCode: "differentPostalcode",
    locationId: "differentLocationId",
    locationMainName: "differentLocationMainName",
    locationName: "differentLocation",
    locationPartName: "differentLocationPartName",
  };

  return (
    <Section title={t("wizard.customer.deliveryAddress")} id="address">
      <AddressForm
        fields={addressFields}
        initialValues={initialValues}
        t={t}
        form={form}
        change={change}
        dataCy="deliveryAddress"
        currentValues={currentValues}
        divider
      />
      <Form.Group>
        <FormLabel
          divider
          width={6}
          id="billingAddressSameAsDeliveryAddress"
          title={t("wizard.customer.billingAddressSameAsDeliveryAddress.label")}
        />
        <Form.Field width={10}>
          <Field
            name="billingAddressSameAsDeliveryAddress"
            component={RadioGroupBooleanField}
            props={{
              options: DeliveryAddressOptions.billingAddressSameAsDeliveryAddress,
              onChange: (value) => {
                // Delivery address same as address
                if (value) {
                  // This should use Object.values, but it is not possible,
                  // see https://github.com/facebook/flow/issues/2221
                  const fields = Object.keys(deliveryfields).map((key) => deliveryfields[key]);

                  fields.forEach((field) => {
                    change(field, null);
                  });
                }
              },
            }}
          />
        </Form.Field>
      </Form.Group>
      <AlternativeDeliveryAddress
        fields={deliveryfields}
        currentValues={currentValues}
        initialValues={initialValues}
        t={t}
        form={form}
        change={change}
        divider
      />
      {!hideSaveButton && (
        <SaveButton
          invalid={invalid}
          submitting={submitting}
          error={submitFailed}
          submitSucceeded={submitSucceeded}
          onClick={handleSubmit}
        />
      )}
    </Section>
  );
};

DeliveryAddress = withTranslation("changeService")(DeliveryAddress);

export const DeliveryAddressForm = reduxForm({
  onSubmit: (values, dispatch) => {
    return sagaConnector(
      "UPDATE_CHANGE_SERVICE_REQUESTED",
      "UPDATE_CHANGE_SERVICE_SUCCEEDED",
      "UPDATE_CHANGE_SERVICE_FAILED",
      {
        changeService: values,
        userId: values.userId,
        id: values.id,
        categoryId: values.categoryId,
      },
      dispatch
    );
  },
  enableReinitialize: true,
})(DeliveryAddress);

export default DeliveryAddress;
